import type { FieldValues, UseFormReturn } from "react-hook-form";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@apac/components/ui";
import type {
  TextFieldProps,
  SelectFieldProps,
  DateFieldProps,
} from "@apac/form-ui";
import { SelectField } from "@apac/form-ui";
import { IconNameEnum, useModuleBEM, useStyles } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  PassportExpiryDateField,
  PassportNumberField,
  useCountryOptions,
  ApacTravelersComponent,
  APAC_TRAVELERS_NAME,
} from "@tenant/apac-travelers";
import { NotificationBanner } from "@tenant/ui";
import defaultStyles from "./styles.module.scss";

export type PassportDetailsFieldOverrides = {
  nationality?: Partial<SelectFieldProps>;
  passportIssuanceCountry?: Partial<SelectFieldProps>;
  passportNumber?: Partial<TextFieldProps<FieldValues>>;
  passportExpiryDate?: Partial<DateFieldProps>;
};

interface PassportDetailsProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  fieldOverrides: PassportDetailsFieldOverrides;
  missingInfoError?: boolean;
}

export const PassportDetails: React.FC<PassportDetailsProps> = ({
  formContext,
  fieldOverrides: {
    nationality = {},
    passportIssuanceCountry = {},
    passportNumber = {},
    passportExpiryDate = {},
  },
  missingInfoError,
}) => {
  const styles = useStyles(
    APAC_TRAVELERS_NAME,
    ApacTravelersComponent.PassportDetails,
    defaultStyles
  );
  const [, cn] = useModuleBEM(styles, ApacTravelersComponent.PassportDetails);
  const { t } = useI18nContext();

  const countryOptions = useCountryOptions();

  return (
    <Accordion defaultValue="passport-details">
      <AccordionItem value="passport-details">
        {missingInfoError && (
          <NotificationBanner
            className={cn("message-banner")}
            iconName={IconNameEnum.warningCircle}
            title={t("apac.form.requiredInfoMessage")}
          />
        )}
        <AccordionTrigger className={cn("trigger")}>
          <div className={cn("title")}>
            {t("travelerForm.passportDetails.title")}
          </div>
        </AccordionTrigger>

        <AccordionContent>
          <div className={cn("container")}>
            <span className={cn("subtitle")}>
              {t("travelerForm.passportDetails.subtitle")}
            </span>

            <SelectField
              formContext={formContext}
              name="nationality"
              label={t("nationality")}
              options={countryOptions}
              placeholder={t("select")}
              {...nationality}
            />
            <SelectField
              formContext={formContext}
              name={"passportIssuanceCountry"}
              label={t(
                "travelerForm.passportDetails.passportIssuanceCountryLabel"
              )}
              placeholder={t("select")}
              options={countryOptions}
              {...passportIssuanceCountry}
            />
            <PassportNumberField
              formContext={formContext}
              name={"passportNumber"}
              label={t("passportNumber")}
              placeholder={t("passportNumber")}
              {...passportNumber}
            />
            <PassportExpiryDateField
              formContext={formContext}
              name={"passportExpiryDate"}
              label={t("travelerForm.passportDetails.passportExpiryDateLabel")}
              {...passportExpiryDate}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
